import type { Option } from "Models";

/** Get first element of array, type-safe */
export function first<T>(
  arr: T[] | readonly T[] | null | undefined,
): T | undefined {
  return arr?.[0];
}

/** Get first key of an object */
export function firstKey<T extends object>(
  obj: T | null | undefined,
): keyof T | undefined {
  return obj ? first(Object.keys(obj) as Array<keyof T>) : undefined;
}

/** Get first value of an object */
export function firstValue<T>(
  obj: Record<string, T> | null | undefined,
): T | undefined {
  return obj ? first(Object.values(obj)) : undefined;
}

/** Get first [key, value] entry of an object */
export function firstEntry<T>(
  obj: Record<string, T> | null | undefined,
): [string, T] | undefined {
  return obj ? first(Object.entries(obj)) : undefined;
}

/** Get numeric keys from an object (for objects with number-like keys) */
export function numericKeys(obj: object | null | undefined): number[] {
  return obj
    ? Object.keys(obj)
        .map(Number)
        .filter((n) => !Number.isNaN(n))
    : [];
}

/** Get first numeric key */
export function firstNumericKey(
  obj: object | null | undefined,
): number | undefined {
  return first(numericKeys(obj));
}

/** Type-safe Object.keys that preserves known key types */
export function typedKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

/** Check if object has any keys (more readable than Object.keys(x).length > 0) */
export function hasKeys(obj: object | null | undefined): boolean {
  return obj ? Object.keys(obj).length > 0 : false;
}

/** Check if object is empty */
export function isEmpty(obj: object | null | undefined): boolean {
  return !hasKeys(obj);
}

export function firstOptionValue<T = boolean | number | string>(
  options: readonly Option<T>[] | null | undefined,
): T | undefined {
  return first(options)?.value;
}

export function firstEnumOptionValue<
  TEnum extends Record<string, string | number>,
>(
  options: readonly Option<TEnum[keyof TEnum]>[] | null | undefined,
): TEnum[keyof TEnum] | undefined {
  return first(options)?.value;
}

export function coalesce<T>(
  ...values: (T | null | undefined)[]
): T | undefined {
  for (const v of values) {
    if (v !== null && v !== undefined) return v;
  }
  return undefined;
}
